import React, { useContext, useState } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import QuantityInput from './QuantityInput';
import ItemPrice from './ItemPrice';
import appLabels from '../../utils/appLabels';
import { SNS_LINK } from '../../utils/constants';
import { get, invoke, find } from 'lodash';
import formatString from '../../utils/formatString';
import Asset from '../Order/Asset';
import { useApp } from '../../context/app-context';
import Modal from '../../common/Modal/Modal';
import SelectItemFrequency from '../Forms/SelectItemFrequency';
import { ItemsContext, ItemsContextType } from './ItemsContext';
import moment from 'moment';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import { Spinner } from '../../common/Spinner/Spinner';

const mainContainerStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f0ee;
`;

const itemContainerStyle = css`
  position: relative;
  display: flex;
`;

const descriptionContainerStyle = css`
  display: flex;
  align-items: flex-start;
`;

const itemInfoStyle = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 1rem;
`;

const dateStyle = css`
  margin-bottom: 1rem;
  color: #000000;
`;

const itemNameStyle = css`
  font-weight: 600 !important;
  margin-bottom: 1rem;
  flex: 1 0 0;
`;

const ItemImageContainer = css`
  background: #ccc;
  width: 96px;
  height: 96px;
  min-width: 96px;
`;

const checkboxRowStyle = css`
  width: fit-content;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  & > svg {
    cursor: pointer;
  }
`;

const ItemWeeks = styled.div`
  cursor: ${(props: { isEditing }) => (props.isEditing ? 'pointer' : 'default')};
  color: ${(props: { isEditing }) => (props.isEditing ? '#7B1A27' : '#2A2928')};
  display: flex;
  align-items: center;
  font-family: 'Nunito Sans', Tahoma, sans-serif;
  margin-bottom: 0.5rem;

  & > svg {
    margin-right: 0.5rem;
  }
`;

ItemWeeks.propTypes = {
  isEditing: PropTypes.bool,
};

const buttonsSectionStyle = css`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e3e3e3;
  padding-top: 0.75rem;

  & > p {
    margin: 0;
  }
`;

const buttonsContainerStyle = css`
  display: flex;
  color: #7b1a27;
  padding-top: 0.75rem;

  & > div {
    border-right: 1px solid #ddd9d5;
  }

  & > div:last-child {
    border-right: none;
  }
`;

const cancelledQuantityStyle = css`
  width: 2.5rem;
  height: 2.5rem;
  border: 0.0625rem solid #ddd9d5;
  border-radius: 6.25rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const rescheduleStyle = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
  text-decoration-line: underline;
  font-weight: 600 !important;
  display: flex;
  align-items: center;
  padding-left: 0;
`;

const rescheduleEditingStyle = css`
  color: #fff;
  cursor: pointer;
  border-radius: 100px;
  background: #7b1a27;
  padding: 8px 16px;
  border: 0;
`;

type OrderItem = {
  nextOrderItemId: number;
  subscriptionId: string;
  productBpn: string;
  productUpc: string;
  itemDescription: string;
  quantity: number;
  itemPrice: number;
  discountedItemPrice: number;
  totalPrice: number;
  //isSubstitutable: boolean,
  frequency: {
    count: number;
    displayFrequency: string;
    unit: string;
  };
  everyWeeks: number;
  cancelledDate: string;
  nextOrderDate: string;
};

const CancelledItem = (props) => {
  const item: OrderItem = get(props, 'item');
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const { showToast, showTooltip } = useApp();
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const frequencyOptions = get(currentItemsContext, 'frequencyOptions', []);
  const [weeks, setWeeks] = useState(
    formatString(appLabels.ScheduledItems.frequencyLabel, {
      weeks: get(item, 'frequency.displayFrequency'),
    }),
  );
  const [quantity, setQuantity] = useState(item.quantity);
  const [selectedFrequency, setSelectedFrequency] = useState<object | undefined>(get(item, 'frequency'));
  // const [isSubstitutable, setIsSubstitutable] = useState(get(item, 'isSubstitutable'));
  const [scheduling, setScheduling] = useState(false);

  const rescheduleItem = async () => {
    const payload = {
      subscriptionId: get(item, 'subscriptionId'),
      frequency: {
        unit: get(selectedFrequency, 'unit'),
        count: get(selectedFrequency, 'count'),
      },
      //"isSubstitutable": isSubstitutable,
      quantity: quantity,
      productBpn: get(item, 'productBpn'),
      storeId: get(currentItemsContext, 'storeId'),
    };

    return await invoke(currentSessionContext, 'ensureActiveSession', async () =>
      invoke(currentItemsContext, 'rescheduleItem', payload),
    );
  };

  const btnHandler = async () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      try {
        setScheduling(true);
        await rescheduleItem();
        showToast('info', `${get(item, 'itemDescription')} successfully scheduled.`);
        analyticsEvents('ar:subscribe-schedule-and-save|confirm-substitutions-for-cancel-items', 'link');
        invoke(currentItemsContext, 'fetchItemsInfo');
      } catch (err) {
        const responseStatusCode = get(err, 'response.status');
        const errorCode = get(err, 'response.data.errors.0.code');

        if (responseStatusCode === 400 && errorCode === 'OSAR_SNS_1036') {
          showToast('error', appLabels.CancelledItems.reSubscriptionError1);
        } else {
          showToast('error', appLabels.CancelledItems.reSubscriptionError2);
        }
      } finally {
        setScheduling(false);
      }
    }
  };

  const openPopoverModal = (evt) => {
    showTooltip({
      message: appLabels.ScheduledItems.popOverText,
      link: SNS_LINK,
      target: get(evt, 'target'),
    });

    evt.stopPropagation();
    analyticsEvents('tool-tip', 'link');
  };

  const analyticsEvents = (name, eventType, message?, updatedFrequency?) => {
    try {
      if (window['AB']?.DATALAYER) {
        if (eventType === 'link') {
          window['AB'].DATALAYER.scheduledAndSaveItemsLinkClick(name, props);
        } else {
          const analyticsObj = {
            productID: props.item.productBpn,
            updatedFrequency: updatedFrequency,
          };
          window['AB'].DATALAYER.scheduledAndSaveItemsModalEvents(eventType, name, analyticsObj, message);
        }
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const weeksClickHandler = () => {
    if (isEditing) {
      setShowFrequencyModal(true);
      analyticsEvents('ar:select-cancel-item-frequency|view', 'view');
    }
  };

  return (
    <div key={item.nextOrderItemId} className={`${mainContainerStyle}`}>
      <div className={`word-highlight-xs ${dateStyle}`}>
        {formatString(appLabels.CanceledItems.cancelledDate, {
          date: moment(get(item, 'cancelledDate', 'YYYY-MM-DD')).format('MMM D'),
        })}
      </div>

      <div className={itemContainerStyle}>
        <div className={ItemImageContainer}>
          <Asset productBpc={item.productBpn} />
        </div>
        <div className={itemInfoStyle}>
          <div className={descriptionContainerStyle}>
            <div className={`body-text ${itemNameStyle}`}>{item.itemDescription}</div>
            {isEditing ? (
              <QuantityInput
                item={item}
                subscriptionId={item.subscriptionId}
                quantity={quantity}
                maxQuantity={20}
                onChange={(q) => setQuantity(q)}
              ></QuantityInput>
            ) : (
              <div className={cancelledQuantityStyle}>{quantity}</div>
            )}
            <ItemPrice unitPrice={item.itemPrice} quantity={quantity} discountPrice={item.totalPrice}></ItemPrice>
          </div>

          <ItemWeeks isEditing={isEditing} onClick={weeksClickHandler}>
            {isEditing ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-repeat"
                viewBox="0 0 16 16"
              >
                <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                <path
                  fillRule="evenodd"
                  d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                />
              </svg>
            ) : null}
            <span className="body-text-sm">{weeks}</span>
          </ItemWeeks>

          {isEditing && (
            <Modal
              titleText="Select item frequency"
              description=""
              onClose={() => setShowFrequencyModal(false)}
              show={showFrequencyModal}
              maxWidth={28}
            >
              <SelectItemFrequency
                frequencyOptions={frequencyOptions}
                submitHandler={(selectedOption) => {
                  //const selected = get(frequencyOptions, replace(selectedOption, /\D/g, ''));
                  const selected = find(frequencyOptions, {
                    displayFrequency: selectedOption,
                  });
                  setSelectedFrequency(selected);
                  setWeeks(
                    formatString(appLabels.ScheduledItems.frequencyLabel, {
                      weeks: get(selected, 'displayFrequency', ''),
                    }),
                  );
                  setShowFrequencyModal(false);
                  if (get(selected, 'count') !== props.item.frequency.count) {
                    analyticsEvents(
                      'ar:select-cancel-item-frequency|change',
                      'click',
                      '',
                      get(selected, 'displayFrequency'),
                    );
                  }
                }}
                itemFrequency={get(selectedFrequency, 'displayFrequency', '')}
              />
            </Modal>
          )}

          <div className={buttonsSectionStyle}>
            {isEditing ? (
              <p className={'body-text-xs'}>
                {formatString(appLabels.CanceledItems.reactivationDate, {
                  date: moment(get(item, 'nextOrderDate', 'YYYY-MM-DD')).format('MMM D'),
                })}
              </p>
            ) : null}

            <div className={buttonsContainerStyle}>
              <button
                className={isEditing ? rescheduleEditingStyle : rescheduleStyle}
                onClick={btnHandler}
                disabled={scheduling}
                aria-label={isEditing ? 'Schedule it' : 'Schedule Item'}
              >
                {scheduling ? 'Scheduling' : isEditing ? 'Schedule it' : 'Schedule item'}
                {scheduling ? (
                  <Spinner
                    className={css`
                      margin-left: 0.5rem;
                    `}
                    size={12}
                  />
                ) : null}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CancelledItem.propTypes = {
  children: PropTypes.any,
  item: PropTypes.object,
  isCancelledItem: PropTypes.bool,
};

export default CancelledItem;
